import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import Image from "next/image";
import CareerHackersLogo from "@/public/images/career hackers.svg";
import { useAuthStore } from "@/store/useAuthStore";
import { useUserNavigation } from "@/hooks/useUserNavigation";

const Login = () => {
  const router = useRouter();

  const fetchUser = useAuthStore((s) => s.fetchUser);
  const login = useAuthStore((s) => s.login);
  const authenticating = useAuthStore((s) => s.authenticating);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const { redirectAfterLogin } = useUserNavigation();

  useEffect(() => {
    if (router.isReady) {
      fetchUser(router);
    }
  }, [router.isReady]);

  useEffect(() => {
    // console.log("Auth state:", { isAuthenticated, user });
    // Only redirect when both authenticated AND user data is available
    if (isAuthenticated && user) {
      redirectAfterLogin();
    }
  }, [isAuthenticated, user, redirectAfterLogin]);

  const [showPassword, setShowPassword] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const toggleShowPassword = () => setShowPassword((prev) => !prev);

  const handleLogin = async () => {
    const email = emailRef.current?.value || "";
    const password = passwordRef.current?.value || "";

    try {
      if (email.trim() === "" || password.trim() === "")
        throw new Error("EmptyException");

      await login(email, password);
    } catch (error: any) {
      if (error.message === "EmptyException") {
        alert("Email or password cannot be empty");
      } else if (error.message === "InvalidException") {
        alert("Invalid email or password");
      } else {
        alert("Something went wrong. Please try again later.");
      }
    }
  };

  useEffect(() => {
    const handleEnter = (e: KeyboardEvent) => {
      if (e.key === "Enter") handleLogin();
    };

    window.addEventListener("keydown", handleEnter);
    return () => window.removeEventListener("keydown", handleEnter);
  }, []);

  if (authenticating) {
    return (
      <div className="fixed inset-0 flex justify-center items-center">
        Loading...
      </div>
    );
  }

  return (
    <div
      className="login-wrapper min-h-screen flex"
      style={{ minHeight: '100vh', height: '100dvh' }}
    >
      <div
        className="section-left px-9 bg-[white] flex flex-col flex-1 min-w-0"
        style={{
          minHeight: 0,
          height: '100%',
          overflowY: 'auto',
        }}
      >
        <div
          className="mt-20 mb-7 flex-shrink-0"
          style={{ minHeight: 0 }}
        >
          <div className="mb-[30px] flex-shrink-0">
            <Image
              src={CareerHackersLogo}
              alt="Career Hackers"
              style={{ marginLeft: "-15px", maxWidth: '180px', height: 'auto', width: '100%' }}
            />
          </div>
          <div className="text-[#747474] text-[14px] w-[90%] mb-10 leading-[1.2] flex-shrink-0">
            Welcome to Career Hackers Talent Solution, please login below to start.
          </div>
        </div>
        <div className="flex-1 flex flex-col min-h-0">
          <form
            className="flex flex-col w-full flex-1 min-h-0"
            style={{ minHeight: 0 }}
            onSubmit={e => { e.preventDefault(); handleLogin(); }}
          >
            <div className="input-container flex-shrink-0">
              <div className="input-title">Email</div>
              <input
                className="styled-input"
                ref={emailRef}
                type="email"
                autoComplete="email"
              />
            </div>

            <div className="input-container flex-shrink-0">
              <div className="input-title">Password</div>
              <div className="relative styled-input">
                <input
                  className="flex-1 w-full"
                  type={showPassword ? "text" : "password"}
                  ref={passwordRef}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="flex-0 px-3 flex items-center text-gray-500"
                  onClick={toggleShowPassword}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            <div className="submit-btn flex-shrink-0 mt-6 mb-8" onClick={handleLogin}>
              Log in
            </div>
            <div className="flex-shrink-0" style={{ minHeight: 0, height: 64 }} />
          </form>
        </div>
      </div>
      <div className="section-right flex-1 min-w-0 min-h-0 relative">
        <div className="cover absolute inset-0 w-full h-full object-cover" style={{ minHeight: 0 }}></div>
      </div>
    </div >
  );
};

export default Login;
